import _ from 'lodash';
import * as members from 'privates/privateMembers';
import reporter from 'privates/reporter';
import * as postMessage from 'privates/postMessage';
import platformUtils from 'platformUtils';
import * as urlUtils from 'privates/urlUtils';
import {validate} from 'privates/validations/validation';
import {capitalize} from 'privates/utils';

const WINDOW_COMP_ID = 'WINDOW';

/**
 * @namespace wix-window
 * @summary The wix-window module contains functionality that pertains to the
 *  current browser window.
 * @description
 *  To use the window module, import `wixWindow` from the `wix-window` module:
 *
 *    ``` javascript
 *    import wixWindow from 'wix-window';
 *    ```
 *
 *  The APIs in `wix-window` can only be used in front-end code.
 */

class Lightbox {

  /**
   * @function getContext
   * @syntax
   * function getContext(): Object
   * @summary Returns the data object that was passed to a lightbox.
   * @description
   *  The `getContext()` function returns the data object passed when the lightbox
   *  was opened using the [`openLightbox()`](wix-window.html#openLightbox) function,
   *  if any data was passed. If the lightbox was opened another way,
   *  `getContext()` returns `undefined`.
   * @snippet [Lightbox-getContext.es6=Get the data that was passed to the lightbox]
   * @returns {external:Object} The data object that was passed to the lightbox.
   * @memberof wix-window.lightbox
   */
  getContext() {
    return platformUtils.getRGI().getLightboxContext();
  }

  /**
   * @function close
   * @syntax
   * function close([data: Object]): void
   * @summary Closes the lightbox.
   * @description
   * The `close` function closes the lightbox and returns the given data to the
   * [`openLightbox()`](wix-window.html#openLightbox) function that was used to
   * open the lightbox.
   * @snippet [Lightbox-close.es6=Close the lightbox]
   * @snippet [Lightbox-close_options.es6=Close the lightbox and pass back data to the opening function]
   * @param {external:Object} [data] The data to pass back to [`openLightbox()`](wix-window.html#openLightbox) function.
   * @memberof wix-window.lightbox
   */
  close(returnedData){
    const contextId = platformUtils.getRGI().getContextId();
    const popups = platformUtils.getRMI('wix-window.lightbox.close').getSiteStructure().lightboxes;
    if (!_.find(popups, {id: contextId})) {
      reporter.reportSdkError(`The current page is not a lightbox and therefore cannot be closed`);
      return;
    }
    postMessage.sendMessage(postMessage.Intent.WIX_CODE_SITE_API, postMessage.MessageTypes.CLOSE_LIGHTBOX, {
      id: contextId,
      returnedData
    });
  }
}

class RenderingContext {
    /**
     * @member renderCycle
     * @label property
     * @syntax
     * get renderCycle(): Number
     * @summary Gets the current cycle in the rendering process.
     * @description
     *  When possible, the rendering process is split in two in order to
     *  improve performance. The first cycle in the process happens server-side
     *  and the second cycle happens client-side. When not possible, all rendering
     *  happens client-side.
     *
     *  When the rendering is split, `renderCycle` returns `1` on the server
     *  and `2` on the client.
     *
     *  When all the rendering happens client-side, `renderCycle` returns `1`
     *  while the rendering process is still running.
     *
     *  In either case, after the client-side rendering has finished,
     *  `renderCycle` returns `NaN`.
     *
     *  Use the `renderCycle` property in your page's [`onReady()`]($w.html#onReady)
     *  event handler to control during which cycles your code runs.
     * @note
     *  Rendering never occurs server-side when previewing your site.
     * @snippet [Rendering-renderCycle.es6=Get the current cycle in the rendering process]
     * @snippet [Rendering-renderCycle_scenario.es6=Use rendering cycle number to selectively run code per cycle]
     * @type {external:Number}
     * @memberof wix-window.rendering
     * @readonly
     **/
    get renderCycle() {
        return Number(platformUtils.getQueryParameter("renderCycle")) || NaN;
    }

    /**
     * @member env
     * @label property
     * @syntax
     * get env(): String
     * @summary Gets the current environment the rendering process is running in.
     * @description
     *  When possible, the rendering process is split in two in order to
     *  improve performance. The first cycle in the process happens server-side
     *  and the second cycle happens client-side. When not possible, all rendering
     *  happens client-side.
     *
     *  The `env` property returns `"backend"` when rendering on the server
     *  and `"browser"` when rendering on the client.
     *
     *  Use the `env` property in your page's [`onReady()`]($w.html#onReady)
     *  event handler to control where your code runs during the rendering
     *  process.
     * @note
     *  Rendering never occurs server-side when previewing your site.
     * @snippet [Rendering-env.es6=Get the current cycle in the rendering process]
     * @snippet [Rendering-env_scenario.es6=Use rendering environment to selectively run code per environment]
     * @type {external:String}
     * @memberof wix-window.rendering
     * @readonly
     **/
    get env() {
        return platformUtils.getQueryParameter("renderingEnv") || "browser";
    }

    get warmupData() {
        const uw = platformUtils.getQueryParameter("userWarmup");
        return uw && uw['wix-code'];
    }
}

class Window {

    constructor(appDefId){
        members._appDefId.set(this, appDefId);
    }

    /**
     * @namespace wix-window.rendering
     * @summary The Rendering API is used to control when code is run as a page is being loaded.
     * @description
     *  When possible, the rendering process that runs when your page loads is
     *  split in two in order to improve performance. The first cycle in the process
     *  happens server-side and the second cycle happens client-side. When not
     *  possible, all rendering happens client-side.
     *
     *  Use the Rendering API to track when and where your code is running to
     *  selectively run parts of your code in certain conditions.
     *
     *  To use the Rendering API, import `wixWindow` from the `wix-window` module:
     *
     *    ``` javascript
     *    import wixWindow from 'wix-window';
     *    ```
     *
     *  To learn more about the rendering process, see [About the Page Rendering Process](https://support.wix.com/en/article/about-the-page-rendering-process).
     */
    get rendering() {
        return new RenderingContext();
    }

    /**
     * @namespace wix-window.lightbox
     * @summary A <a href="https://support.wix.com/en/article/about-lightboxes" target="_blank">lightbox</a> pops up on your site to grab your visitor's attention.
     * @description
     *  To use the Lightbox API, import `wixWindow` from the `wix-window` module:
     *
     *    ``` javascript
     *    import wixWindow from 'wix-window';
     *    ```
     *
     * The APIs in `wix-window.lightbox` can only be used in front-end code.
     */
    get lightbox() {
        return new Lightbox();
    }

    /**
     * @member viewMode
     * @label property
     * @syntax
     * get viewMode(): "Preview" | "Site"
     * @summary Gets which mode the site is currently being viewed in.
     * @description
     *  The `viewMode` property gets either:
     *
     *  + `"Preview"` - When previewing the site using the **Preview** button in the Editor.
     *  + `"Site"` - When viewing the published site.
     * @snippet [Window-viewMode.es6=Get a window's view mode]
     * @type {external:String}
     * @memberof wix-window
     * @readonly
     */
    get viewMode() {
        return capitalize(platformUtils.getQueryParameter('viewMode'));
    }

    /**
     * @member formFactor
     * @label property
     * @syntax
     * get formFactor(): "Desktop" | "Mobile"
     * @summary Gets what kind of device is being used to view the page.
     * @description
     *  The `formFactor` property gets either:
     *
     *  + `"Desktop"` - When viewed in a desktop browser.
     *  + `"Mobile"`  - When viewed in a mobile browser.
     * @snippet [Window-formFactor.es6=Get a device's form factor]
     * @type {external:String}
     * @memberof wix-window
     * @readonly
     */
    get formFactor() {
        const formFacotr = platformUtils.getRGI().getDeviceType() || 'desktop';
        return capitalize(formFacotr);
    }

    /**
     * @member locale
     * @label property
     * @syntax
     * get locale(): String
     * @summary Gets the locale of the current environment.
     * @description
     *  A locale, also known as an IETF language tag, is an abbreviated code
     *  that defines the user's language, country, and other aspects of the user
     *  interface such as number format and date format.
     *
     *  Some common locales include:
     *  + `"en"` - English
     *  + `"es"` - Spanish
     *  + `"de"` - German
     *  + `"ja"` - Japanese
     *  + `"fr"` - French
     *  + `"it"` - Italian
     * @snippet [Window-locale.es6=Get an environment's locale]
     * @type {external:String}
     * @memberof wix-window
     * @readonly
     */
    get locale() {
        return platformUtils.getQueryParameter("locale");
    }

    /**
     * @member referrer
     * @label property
     * @syntax
     * get referrer(): String
     * @summary Gets the HTTP referrer header field.
     * @description
     *  The `referrer` is the address of the previous web page that the user was
     *  on before arriving at the current page, typically by clicking a link.
     * @snippet [Window-referrer.es6=Get the referrer information]
     * @type {external:String}
     * @memberof wix-window
     * @readonly
     **/
    get referrer() {
        return platformUtils.getQueryParameter("referrer");
    }

    /**
     * @typedef {external:Object} wix-window~WindowSizeInfo
     * @summary An object returned by the `getBoundingRect()` function that contains information about the window's size, the document's size, and the current scroll position.
     * @property {external:Object} window An object with `height` and `width` key:value pairs containing the size of the viewable area of the current browser window.
     * @property {external:Object} document An object with `height` and `width` key:value pairs containing the size of the actual body of the page, which may be larger or smaller than the current window.
     * @property {external:Object} scroll An object with `x` and `y` key:value pairs containing the scroll offset of the page within the window from the top-left corner.
     * @snippet [Window-getBoundingRect.es6=Get information about the window]
     * @see [getBoundingRect( )](#getBoundingRect)
     */

    /**
     * @function getBoundingRect
     * @syntax
     * function getBoundingRect(): Promise&lt;WindowSizeInfo&gt;
     * @summary Returns information about the window.
     * @description
     *  The `getBoundingRect()` function returns a Promise that resolves to an object
     *  containing information about the current window's size, the document's size,
     *  and the current scroll position.
     * @returns {Promise}
     * @fulfill {wix-window~WindowSizeInfo} An object containing information about the window's size, the document's size, and the current scroll position.
     * @snippet [Window-getBoundingRect.es6=Get information about the window]
     * @memberof wix-window
     */
     getBoundingRect() {
        return new Promise((resolve, reject) => {
            postMessage.sendMessage(postMessage.Intent.WIX_CODE_SITE_API, postMessage.MessageTypes.WINDOW_BOUNDING_RECT, {}, (data, error) => {
                if (error) {
                    reject(error);
                } else {
                    resolve(data);
                }
            });
        });
    }


    /**
     * @function scrollTo
     * @syntax
     * function scrollTo(x: Number, y: Number): Promise&lt;void&gt;
     * @summary Scrolls the page to a specific location.
     * @description
     *  The `scrollTo()` function returns a Promise that resolves when the current
     *  page has been scrolled to the given location.
     *
     *  The `x` and `y` parameters determine the top-left pixel that is
     *  displayed on screen after the scroll.
     *
     *  To scroll to a specific element on the page, see the `$w.Node`
     *  [`scrollTo()`]($w.Node.html) function.
     * @snippet [Window-scrollTo.es6=Scroll the page to a location]
     * @snippet [Window-scrollTo_then.es6=Scroll the page to a location and log message when done]
     * @param {external:Number} x The horizontal position, in pixels, to scroll to.
     * @param {external:Number} y The vertical position, in pixels, to scroll to.
     * @returns {Promise}
     * @fulfill {void} When the scroll is complete.
     * @memberof wix-window
     */
    scrollTo(x, y) {
        const validtionObj = {};
        if (!_.isNil(x)) {
            validtionObj.x = x;
        }
        if (!_.isNil(y)) {
            validtionObj.y = y;
        }
        return new Promise((resolve, reject) => {
            if (!validate({
                compType: 'Window',
                functionName: 'scrollTo',
                dataToValidate: validtionObj
            })) {
                reject({});
                return;
            }
            const args = {x, y, scrollAnimation: true, requiresDom: {compId: WINDOW_COMP_ID}};
            postMessage.sendMessage(postMessage.Intent.WIX_CODE_SITE_API, postMessage.MessageTypes.SCROLL_TO, args, (data, err) => {
                if (err) {
                    reject(err);
                } else {
                    resolve(data);
                }
            });
        });
    }

    /**
     * @function scrollBy
     * @syntax
     * function scrollBy(x: Number, y: Number): Promise&lt;void&gt;
     * @summary Scrolls the page by a given number of pixels.
     * @description
     *  The `scrollBy()` function returns a Promise that resolves when the current
     *  page has been scrolled by the given number of pixels,
     *
     *  The `x` and `y` parameters determine the number of horizontal and vertical
     *  pixels to scroll the current page. Negative numbers scroll up or to the
     *  left and positive numbers scroll down or to the right.
     * @snippet [Window-scrollBy.es6=Scroll the page by a given number of pixels]
     * @snippet [Window-scrollBy_then.es6=Scroll the page by a given number of pixels and log message when done]
     * @param {external:Number} x The horizontal offset, in pixels, to scroll by.
     * @param {external:Number} y The vertical offset, in pixels, to scroll by.
     * @returns {Promise}
     * @fulfill {void} When the scroll is complete.
     * @memberof wix-window
     */
    scrollBy(x, y) {
        if (_.isNil(x)) {
            x = 0;
        }
        if (_.isNil(y)) {
            y = 0;
        }
        return new Promise((resolve, reject) => {
            const args = {x, y, requiresDom: {compId: WINDOW_COMP_ID}};
            if (!validate({
                compType: 'Window',
                functionName: 'scrollBy',
                dataToValidate: {x, y}
            })) {
                reject({});
                return;
            }
            postMessage.sendMessage(postMessage.Intent.WIX_CODE_SITE_API, postMessage.MessageTypes.SCROLL_BY, args, (data, err) => {
                if (err) {
                    reject(err);
                }
                resolve(data);
            });
        });
    }

    /**
     * @function getRouterData
     * @syntax
     * function getRouterData(): Object
     * @summary Returns the data that a router passed to the page in its response.
     * @returns {external:Object} The data returned by the router.
     * @description
     *  When you create a router and define its functionality in its [router()](wix-router.html#router)
     *  function, you can choose to send data along with the router's [response](wix-router.WixRouterResponse.html).
     *  That data is retrieved in the code of the page that was routed to using the
     *  `getRouterData()` function.
     *
     *  If you call the `getRouterData()` function from a non-router page or a router
     *  page that wasn't sent any data, the function returns `null`.
     * @memberof wix-window
     * @snippet [Window-getRouterData.es6=Get the data passed by a router]
     * @see [router( )](wix-router.html#router)
     * @see [WixRouterResponse](wix-router.WixRouterResponse.html)
     */
    getRouterData() {
        return self.routerData || null;
    }

    /**
     * @function getCurrentGeolocation
     * @syntax
     * function getCurrentGeolocation(): Promise&lt;Object&gt;
     * @summary Returns the current geolocation of the user.
     * @description
     *  The `getCurrentGeolocation()` function returns a Promise that resolves to an
     *  object containing the current geolocation of the user.
     *
     *  The object contains the following key:value pairs:
     *  + `"timestamp"` - The geolocation timestamp representing the date and time at which the location was retrieved.
     *  + `"coords"` - An object that defines the location.
     *    + `"latitude"` - The position's latitude in decimal degrees.
     *    + `"longitude"` - The position's longitude in decimal degrees.
     *    + `"altitude"` - The position's altitude in metres, relative to sea level. This value may be null if the browser cannot provide the data.
     *    + `"accuracy"` - The accuracy in meters of the latitude and longitude properties.
     *    + `"altitudeAccuracy"` - The accuracy in meters of the altitude property. This value may be null.
     *    + `"heading"` - The direction in degrees in which the device is traveling. It indicates how far off from heading true north the device is. If `speed` is `0`, the heading is `NaN`. This value may be null if the browser cannot provide the data.
     *    + `"speed"` - The velocity in meters per second of the device. This value may be null if the browser cannot provide the data.
     *
     *
     * @note
     *  The `getCurrentGeolocation()` function has the following limitiations:
     *  + On Chrome, the function only works on HTTPS sites.
     *  + On Chrome, Firefox, and Safari, the function only works if the user
     *    approves a popup.
     * @snippet [Window-getCurrentGeolocation.es6=Get the geolocation data]
     * @returns {Promise}
     * @fulfill {external:Object} The coordinates and timestamp of the current location.
     * @reject {String} The user blocked the geolocation popup.
     * @memberof wix-window
     */
    getCurrentGeolocation () {
        return new Promise((resolve, reject) => {
            postMessage.sendMessage(postMessage.Intent.WIX_CODE_SITE_API, postMessage.MessageTypes.GET_CURRENT_GEOLOCATION, null, (data, error) => {
                if (error) {
                    reject(error);
                } else {
                    resolve(data);
                }
            });
        });
    }

    /**
     * @function openLightbox
     * @syntax
     * function openLightbox(name: String[, data: Object]): Promise&lt;Object&gt;
     * @summary Opens a lightbox and optionally passes it the given data.
     * @description
     *  The `openLightbox()` function returns a Promise which is resolved when the
     *  lightbox closes. If the lightbox is closed programmatically using its
     *  [`close()`](wix-window.lightbox.html#close) function, and the
     *  [`close()`](wix-window.lightbox.html#close) function was invoked
     *  with a data parameter, then the Promise resolves to that data object.
     *
     *  If you send a data object to the lightbox, use the [`getContext()`](#getContext)
     *  function in the lightbox's code to access the received data.
     * @note
     *  Use the name of the lightbox and not the lightbox's ID when calling
     *  `openLightbox()`.
     * @snippet [Window-openLightbox.es6=Open a lightbox]
     * @snippet [Window-openLightbox_options.es6=Open a lightbox and send it data]
     * @snippet [Window-openLightbox_then.es6=Open a lightbox and receive data when it is closed]
     * @snippet [Window-openLightbox_options_then.es6=Open a lightbox, send it data, and receive data back when it is closed]
     * @param {external:String} name The name of the lightbox to open.
     * @param {external:Object} [data] The data to pass to the lightbox.
     * @returns {external:Promise}
     * @fulfill {external:Object} The returned data from the lightbox.
     * @reject {external:Error} The error that caused the rejection.
     * @memberof wix-window
     * @see [openModal( )](#openModal)
     */
    openLightbox (name, data) {
        return new Promise((resolve, reject) => {
            const isValid = validate({
                compType: 'Lightbox',
                functionName: 'openLightbox',
                dataToValidate: {name}
            });
            if (!isValid) {
                return reject('Lightbox title is not a valid input');
            }
            const popups = platformUtils.getRMI('wix-window.openLightbox').getSiteStructure().lightboxes;
            const popupInfo = _.find(popups, {name});


            if (!_.has(popupInfo, 'id')) {
                return reject(`There is no lightbox with the title "${name}".`);
            }
            const message = {id: popupInfo.id};
            if (!_.isUndefined(data)) {
                message.context = data;
            }
            postMessage.sendMessage(postMessage.Intent.WIX_CODE_SITE_API, postMessage.MessageTypes.OPEN_LIGHTBOX, message, (data, err) => {
                if (err) {
                    reject(err);
                } else {
                    resolve(data);
                }
            });
        });
    }

    /**
     * @namespace wix-window.trackingParameters
     * @summary Objects used when calling the [`trackEvent()`](wix-window.html#trackEvent) function.
     */

    /**
     * @typedef {external:Object} wix-window.trackingParameters~AddPaymentInfoEvent
     * @summary An object used when sending an AddPaymentInfo track event.
     * @property {external:String} option Describes option information on a checkout page, such as selected payment method.
     * @snippet [Window-trackEvent-AddPaymentInfo.es6=Send an AddPaymentInfo trackEvent]
     * @see [trackEvent( )](#trackEvent)
     */

    /**
     * @typedef {external:Object} wix-window.trackingParameters~AddProductImpressionEvent
     * @summary An object used when sending an AddProductImpression track event.
     * @property {wix-window.trackingParameters~AddProductImpressionEventContents[]} contents Product information.
     * @snippet [Window-trackEvent-AddProductImpression.es6=Send an AddProductImpression trackEvent]
     * @see [trackEvent( )](#trackEvent)
     */

    /**
     * @typedef {external:Object} wix-window.trackingParameters~AddProductImpressionEventContents
     * @summary An object used when sending an AddProductImpression track event.
     * @property {external:String} [id] - Product ID.
     * @property {external:String} [sku] - Stock keeping unit code.
     * @property {external:String} name - Product name.
     * @property {external:String} [category] - Category of the page or product.
     * @property {external:Number} [price] - Product price.
     * @property {external:String} [currency] - Currency code.
     * @property {external:String} [brand] - Brand name of the product.
     * @property {external:String} [variant] - Variant of the product.
     * @property {external:String} [list] - List or collection the product is in.
     * @property {external:String} [position] - Product's position in a list or collection.
     * @snippet [Window-trackEvent-AddProductImpression.es6=Send an AddProductImpression trackEvent]
     * @see [trackEvent( )](#trackEvent)
     */

    /**
     * @typedef {external:Object} wix-window.trackingParameters~AddToCartEvent
     * @summary An object used when sending an AddToCart track event.
     * @property {external:String} [id] - Product ID.
     * @property {external:String} [sku] - Stock keeping unit code.
     * @property {external:String} name - Product name.
     * @property {external:Number} [price] - Product price.
     * @property {external:String} [currency] - Currency code.
     * @property {external:String} [category] - Category of the page or product.
     * @property {external:String} [brand] - Brand name of the product.
     * @property {external:String} [variant] - Variant of the product.
     * @property {external:String} [position] - Product's position in a list or collection.
     * @property {external:Number} [quantity] - Product quantity.
     * @snippet [Window-trackEvent-AddToCart.es6=Send an AddToCart trackEvent]
     * @see [trackEvent( )](#trackEvent)
     */

    /**
     * @typedef {external:Object} wix-window.trackingParameters~ClickProductEvent
     * @summary An object used when sending a ClickProduct track event.
     * @property {external:String} [id] - Product ID.
     * @property {external:String} [sku] - Stock keeping unit code.
     * @property {external:String} name - Product name.
     * @property {external:Number} [price] - Product price.
     * @property {external:String} [currency] - Currency code.
     * @property {external:String} [category] - Category of the page or product.
     * @property {external:String} [brand] - Brand name of the product.
     * @property {external:String} [variant] - Variant of the product.
     * @property {external:String} [list] - List or collection the product is in.
     * @property {external:String} [position] - Product's position in a list or collection.
     * @snippet [Window-trackEvent-ClickProduct.es6=Send a ClickProduct trackEvent]
     * @see [trackEvent( )](#trackEvent)
     */

    /**
     * @typedef {external:Object} wix-window.trackingParameters~CustomEvent
     * @summary An object used when sending a CustomEvent track event.
     * @description
     *  The object contains one required property, `event`, and any number of
     *  custom properties.
     *
     *  When sending a custom event to Google Analytics, include following parameters:
     *  + `eventCategory` - string - object that was interacted with
     *  + `eventAction` - string - type of interaction
     *  + `eventLabel` (optional) - string - event category
     *  + `eventValue` (optional) - integer - numeric value associated with the event
     * @property {external:String} event - Product ID.
     * @snippet [Window-trackEvent-CustomEvent.es6=Send a CustomEvent trackEvent]
     * @see [trackEvent( )](#trackEvent)
     */

    /**
     * @typedef {external:Object} wix-window.trackingParameters~InitiateCheckoutEvent
     * @summary An object used when sending an InitiateCheckout track event.
     * @property {wix-window.trackingParameters~InitiateCheckoutEventContents[]} contents Product information.
     * @snippet [Window-trackEvent-InitiateCheckout.es6=Send an InitiateCheckout trackEvent]
     * @see [trackEvent( )](#trackEvent)
     */

    /**
     * @typedef {external:Object} wix-window.trackingParameters~InitiateCheckoutEventContents
     * @summary An object used when sending an InitiateCheckout track event.
     * @property {external:String} [id] - Product ID.
     * @property {external:String} [sku] - Stock keeping unit code.
     * @property {external:String} name - Product name.
     * @property {external:String} [category] - Category of the page or product.
     * @property {external:Number} [price] - Product price.
     * @property {external:String} [currency] - Currency code.
     * @property {external:String} [brand] - Brand name of the product.
     * @property {external:String} [variant] - Variant of the product.
     * @property {external:Number} [quantity] - Product quantity.
     * @snippet [Window-trackEvent-InitiateCheckout.es6=Send an InitiateCheckout trackEvent]
     * @see [trackEvent( )](#trackEvent)
     */

    /**
     * @typedef {external:Object} wix-window.trackingParameters~PurchaseEvent
     * @summary An object used when sending a Purchase track event.
     * @property {external:String} [id] - Product ID.
     * @property {external:String} [affiliation] - Name of the store where the event occurred.
     * @property {external:Number} [revenue] - Total amount of the purchase including shipping, taxes, etc.
     * @property {external:Number} [tax] - Total tax charge.
     * @property {external:Number} [shipping] - Total shipping charge.
     * @property {external:String} [coupon] - Coupon code applied to the transaction.
     * @property {wix-window.trackingParameters~PurchaseEventContents[]} contents Product information.

     * @snippet [Window-trackEvent-Purchase.es6=Send a Purchase trackEvent]
     * @see [trackEvent( )](#trackEvent)
     */

    /**
     * @typedef {external:Object} wix-window.trackingParameters~PurchaseEventContents
     * @summary An object used when sending an Purchase track event.
     * @property {external:String} [id] - Product ID.
     * @property {external:String} name - Product name.
     * @property {external:String} [category] - Category of the page or product.
     * @property {external:Number} [price] - Product price.
     * @property {external:String} [currency] - Currency code.
     * @property {external:String} [brand] - Brand name of the product.
     * @property {external:String} [variant] - Variant of the product.
     * @property {external:Number} [quantity] - Product quantity.
     * @snippet [Window-trackEvent-Purchase.es6=Send a Purchase trackEvent]
     * @see [trackEvent( )](#trackEvent)
     */

    /**
     * @typedef {external:Object} wix-window.trackingParameters~RemoveFromCartEvent
     * @summary An object used when sending a RemoveFromCart track event.
     * @property {external:String} [id] - Product ID.
     * @property {external:String} name - Product name.
     * @property {external:Number} [price] - Product price.
     * @property {external:String} [currency] - Currency code.
     * @property {external:String} [category] - Category of the page or product.
     * @property {external:String} [brand] - Brand name of the product.
     * @property {external:String} [variant] - Variant of the product.
     * @property {external:String} [position] - Product's position in a list or collection.
     * @property {external:Number} [quantity] - Product quantity.
     * @snippet [Window-trackEvent-RemoveFromCart.es6=Send a RemoveFromCart trackEvent]
     * @see [trackEvent( )](#trackEvent)
     */

    /**
     * @typedef {external:Object} wix-window.trackingParameters~ViewContentEvent
     * @summary An object used when sending a ViewContent track event.
     * @property {external:String} [id] - Product ID.
     * @property {external:String} [sku] - Stock keeping unit code.
     * @property {external:String} name - Product name.
     * @property {external:Number} [price] - Product price.
     * @property {external:String} [currency] - Currency code.
     * @property {external:String} [category] - Category of the page or product.
     * @property {external:String} [brand] - Brand name of the product.
     * @property {external:String} [variant] - Variant of the product.
     * @property {external:String} [list] - List or collection the product is in.
     * @property {external:String} [position] - Product's position in a list or collection.
     * @snippet [Window-trackEvent-ViewContent.es6=Send a ViewContent trackEvent]
     * @see [trackEvent( )](#trackEvent)
     */

    /**
     * @function trackEvent
     * @syntax
     * function trackEvent(eventName: TrackEvent, parameters: trackingParameters): void
     * type TrackEvent = "AddPaymentInfo" | "AddProductImpression"
     * | "AddToCart" | "ClickProduct" | "CustomEvent"
     * | "InitiateCheckout" | "Lead" | "Purchase" | "RemoveFromCart"
     * | "ViewContent"
     * type trackingParameters = AddPaymentInfoEvent
     * | AddProductImpressionEvent | AddToCartEvent | ClickProductEvent
     * | CustomEvent | InitiateCheckoutEvent | PurchaseEvent
     * | RemoveFromCartEvent | ViewContentEvent
     * @summary Sends a tracking event to external analytics tools.
     * @description
     *  The `trackEvent()` function sends an event to analytics tools connected
     *  to your site. It can send events to Google Analytics and Facebook Pixel.
     *  To learn how to connect analytics tools to your site, see
     *  [About Tracking Tools & Analytics](https://support.wix.com/en/article/about-tracking-tools-analytics).
     *
     *  The following events and parameters are available:
     *
     *  + `"AddProductImpression"` - [parameters](wix-window.trackingParameters.html#AddProductImpressionEvent) - When a user views a product. (Google Analytics only)
     *  + `"ClickProduct"` - [parameters](wix-window.trackingParameters.html#ClickProductEvent) - When a user clicks on a product. (Google Analytics only)
     *  + `"ViewContent"` - [parameters](wix-window.trackingParameters.html#ViewContentEvent) - When a key page is viewed.
     *  + `"AddToCart"` - [parameters](wix-window.trackingParameters.html#AddToCartEvent) - When a user adds a product to the shopping cart.
     *  + `"RemoveFromCart"` - [parameters](wix-window.trackingParameters.html#RemoveFromCartEvent) - When a user removes a product from the shopping cart. (Google Analytics only)
     *  + `"InitiateCheckout"` - [parameters](wix-window.trackingParameters.html#InitiateCheckoutEvent) - When a user starts the checkout process.
     *  + `"AddPaymentInfo"` - [parameters](wix-window.trackingParameters.html#AddPaymentInfoEvent) - When a user saves payment information.
     *  + `"Purchase"` - [parameters](wix-window.trackingParameters.html#PurchaseEvent) - When the checkout process is complete.
     *  + `"Lead"` - When a user subscribes to a newsletter or submits a contact form.
     *  + `"CustomEvent"` - [parameters](wix-window.trackingParameters.html#CustomEvent) - When a user performs an event not listed above.
     *
     *
     * @note
     *  The `trackEvent()` function only runs on published versions of your site.
     *  It does not work when previewing your site.
     * @snippet [Window-trackEvent-ViewContent.es6=Send a trackEvent with parameters]
     * @snippet [Window-trackEvent-Lead.es6=Send a trackEvent that doesn't take any parameters]
     * @snippet [Window-trackEvent-onReady.es6=Send a trackEvent from the onReady event handler]
     * @param {external:String} eventName The name of the event.
     * @param {wix-window.trackingParameters~AddPaymentInfoEvent | wix-window.trackingParameters~AddProductImpressionEvent | wix-window.trackingParameters~AddToCartEvent | wix-window.trackingParameters~ClickProductEvent | wix-window.trackingParameters~CustomEvent | wix-window.trackingParameters~InitiateCheckoutEvent | wix-window.trackingParameters~PurchaseEvent | wix-window.trackingParameters~RemoveFromCartEvent | wix-window.trackingParameters~ViewContentEvent} parameters The event's parameters.
     * @memberof wix-window
     */
    trackEvent(eventName, data, options) {
        const validtionObj = {data, options};
        if (!_.isNil(eventName)) {
            validtionObj.eventName = eventName;
        }
        if (!validate({
            compType: 'trackEvent',
            functionName: 'trackEvent',
            dataToValidate: validtionObj,
            shouldCheckMandatoryFields: true
        })) {
            return;
        }

        const message = {eventName};
        if (!_.isNil(data)) {
            message.params = data;
        }
        if (!_.isNil(options)) {
            message.options = options;
        }
        postMessage.sendMessage(postMessage.Intent.WIX_CODE_SITE_API, postMessage.MessageTypes.TRACK_EVENT, message);
    }

    /**
     * @typedef {external:Object} wix-window~OpenModalOptions
     * @summary An object used when opening a modal window.
     * @property {external:Number} width - Width of the modal window.
     * @property {external:Number} height - Height of the modal window.
     * @snippet [Window-openModal.es6=Open a modal window]
     * @see [openModal( )](#openModal)
     */

    /**
     * @function openModal
     * @syntax
     * function openModal(url: String, options: OpenModalOptions): Promise&lt;void&gt;
     * @summary Opens a modal window that displays the specified web page.
     * @description
     *  A modal window displays the page specified by the `url` property over
     *  your current page. Unlike a [lightbox](wix-window.lightbox.html), which
     *  is open using the [`openLightbox()`](#openLightbox) function, the window
     *  opened by `openModal()` is not part of your site structure.
     *
     *  Only one modal window can be open at any given time. Therefore, opening
     *  a modal window closes an already open modal window if there is one.
     * @note
     *  The specified `url` must be an HTTPS URL. To use an HTTP URL, [turn off
     *  SSL for your site](https://support.wix.com/en/article/turning-ssl-on-or-off-for-your-site).
     * @snippet [Window-openModal.es6=Open a modal window]
     * @snippet [Window-openModal_then.es6=Open a modal window and log a message when it is closed]
     * @param {external:String} url The URL of the page to show in the modal window.
     * @param {wix-window~OpenModalOptions} options The options used for the modal window.
     * @returns {external:Promise}
     * @fulfill {void} When the modal window is closed.
     * @reject {external:Error} The error that caused the rejection.
     * @memberof wix-window
     * @see [openLightbox( )](#openLightbox)
     */
    openModal(url, options) {
        return new Promise((resolve, reject) => {
            const isValid = validate({
                compType: 'modal',
                functionName: 'openModal',
                dataToValidate: {url, options},
                shouldCheckMandatoryFields: true
            });
            if (!isValid) {
                return reject('Modal arguments are not defined correctly');
            }

            const modalProtocol = urlUtils.parseUrl(url).protocol;
            const RGI = platformUtils.getRGI();
            const siteUrl = urlUtils.getUrlFromRGI(RGI);
            const siteProtocol = urlUtils.parseUrl(siteUrl).protocol;

            if(modalProtocol === 'http' && siteProtocol === 'https') {
                return reject(`Mix protocol is not allowed. Modal url protocol is ${modalProtocol}, and the page is ${siteProtocol}`);
            }

            const {width, height, theme} = options;
            postMessage.sendMessage(postMessage.Intent.WIX_CODE_SITE_API, postMessage.MessageTypes.OPEN_MODAL, {
                url,
                width,
                height,
                theme
            }, (data, error) => {
                if (error) {
                    reject(error);
                } else {
                    resolve(data);
                }
            });
        });
    }

    /**
     * @function postMessage
     * @syntax
     * function postMessage(message: Object[, target: String]): void
     * @summary Sends a message to the page's parent.
     * @description
     *  If a page is embedded within another site, using an [HtmlComponent]($w.HtmlComponent.html)
     *  on a Wix site or an [iframe](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe)
     *  on a non-Wix site, you can use the `postMessage()` function to send a
     *  message from the inner site to the outer site.
     *
     *  When the parent site is a Wix site, use the [`onMessage()`]($w.HtmlComponent.html#onMessage)
     *  function to receive the message on the parent page.
     *
     *  When the parent site is a non-Wix site, use the page's `window.onmessage`
     *  event handler to read the `data` property of the received `MessageEvent`
     *  to receive the message on the parent page.
     * @snippet [Window-postMessage_wix.es6=Send a message to a Wix parent site]
     * @snippet [Window-postMessage_nonwix.es6=Send a message to a non-Wix parent site]
     * @param {external:Object} message The message to send.
     * @param {external:String} [target] The target to send the message to. Must be `"parent"` or omitted. Defaults to `"parent"`.
     * @returns {external:Promise}
     * @fulfill {external:Object} The data returned from the modal window.
     * @reject {external:Error} The error that caused the rejection.
     * @memberof wix-window
     */
    postMessage(payload, target){
        if (!validate({
            compType: 'Window',
            functionName: 'postMessage',
            dataToValidate: {payload, target}
        })) {
            return;
        }
        postMessage.sendMessage(postMessage.Intent.WIX_CODE_SITE_API, postMessage.MessageTypes.SEND_MESSAGE_TO_PARENT, payload);
    }
}

export default Window;



// WEBPACK FOOTER //
// ./js/modules/namespaces/window.es6